import { useCallback } from "react";

import { otherDialDevice } from "features/calls/dialstack/softphone/use-dial-device-preference.hook";
import {
  toOutboundCallPatient,
  useDialstackSoftphone
} from "features/calls/dialstack/softphone/use-dialstack-softphone.hook";
import type { CallContact } from "services/dialstack";
import type { Patient } from "services/types/patients.type";

import { normalizePhoneNumber } from "../utils";
import { dialDeviceTooltip } from "./dial-device-copy";

// The part of a click a dial surface passes along: holding ⌥/Alt flips the
// call to the user's other device for this one call (ENG-2183).
export type MakeCallEvent = { altKey?: boolean };

// Who a click-to-call is to: a patient (names the call in the softphone and
// attributes the Call row) or a Rolodex contact (ENG-2177). A contact dial
// carries no patient context; the outgoing-call card names the contact from
// the backend's number match, which knows the contact's numbers.
export type CallParty = Partial<Patient> | { contact: CallContact };

export function isContactParty(
  party: CallParty | undefined
): party is { contact: CallContact } {
  return Boolean(party && "contact" in party && party.contact);
}

// The one dial path for click-to-call surfaces. Without a Voice seat it opens
// the softphone's notice on how to get one instead of dialling (ENG-2315);
// otherwise a no-op when the user has no connected softphone (callers gate
// visibility on the practice's Voice).
export default function useMakeCall() {
  const {
    enabled: isSoftphoneEnabled,
    needsVoiceSeat,
    openDialer,
    dialNumber,
    dialDevice,
    devices
  } = useDialstackSoftphone();

  return useCallback(
    (phoneNumber: string, party?: CallParty, event?: MakeCallEvent) => {
      if (needsVoiceSeat) {
        openDialer();
        return;
      }
      if (!isSoftphoneEnabled || !phoneNumber) return;
      const normalizedPhoneNumber = normalizePhoneNumber(phoneNumber);
      // The modifier means nothing to someone with only a computer.
      const device =
        event?.altKey && devices.length > 0
          ? otherDialDevice(dialDevice)
          : undefined;
      const destination = `+1${normalizedPhoneNumber}`;
      const outboundPatient = isContactParty(party)
        ? undefined
        : toOutboundCallPatient(party);
      if (device) dialNumber(destination, outboundPatient, { device });
      else dialNumber(destination, outboundPatient);
    },
    [
      devices.length,
      dialDevice,
      dialNumber,
      isSoftphoneEnabled,
      needsVoiceSeat,
      openDialer
    ]
  );
}

// Tooltip copy for a dial surface, telling the user which device a click will
// use and how to use the other one.
export function useDialDeviceTooltip(action: string) {
  const { dialDevice, devices } = useDialstackSoftphone();
  return dialDeviceTooltip(action, dialDevice, devices);
}
